import { useState, useCallback, useRef } from 'react';

const useExamState = () => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [flaggedQuestions, setFlaggedQuestions] = useState(new Set());
  const [isNavigatorVisible, setIsNavigatorVisible] = useState(false);
  const [examStartTime] = useState(Date.now());
  const originalQuestionsRef = useRef([]);

  const shuffleArray = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  const initializeExam = useCallback((questionsData, shuffleQuestions = true) => {
    // Store original questions for reference
    originalQuestionsRef.current = questionsData;
    
    // Shuffle questions if enabled
    const processedQuestions = shuffleQuestions 
      ? shuffleArray(questionsData) 
      : questionsData;
    
    setQuestions(processedQuestions);
    setCurrentQuestion(0);
    setAnswers({});
    setFlaggedQuestions(new Set());
    setIsNavigatorVisible(false);
  }, [shuffleArray]);

  const selectAnswer = useCallback((questionIndex, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  }, []);

  const toggleFlag = useCallback((questionIndex) => {
    setFlaggedQuestions(prev => {
      const newFlags = new Set(prev);
      if (newFlags.has(questionIndex)) {
        newFlags.delete(questionIndex);
      } else {
        newFlags.add(questionIndex);
      }
      return newFlags;
    });
  }, []);

  const goToQuestion = useCallback((questionIndex) => {
    if (questionIndex >= 0 && questionIndex < questions.length) {
      setCurrentQuestion(questionIndex);
    }
  }, [questions.length]);

  const goToNext = useCallback(() => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  }, [currentQuestion, questions.length]);

  const goToPrevious = useCallback(() => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  }, [currentQuestion]);

  const toggleNavigator = useCallback(() => {
    setIsNavigatorVisible(prev => !prev);
  }, []);

  const getExamSummary = useCallback(() => {
    const totalQuestions = questions.length;
    const answeredQuestions = Object.keys(answers).length;
    const flaggedCount = flaggedQuestions.size;
    const unansweredQuestions = totalQuestions - answeredQuestions;
    const timeSpent = Math.floor((Date.now() - examStartTime) / 1000);

    return {
      totalQuestions,
      answeredQuestions,
      unansweredQuestions,
      flaggedCount,
      timeSpent,
      completionPercentage: Math.round((answeredQuestions / totalQuestions) * 100)
    };
  }, [questions.length, answers, flaggedQuestions.size, examStartTime]);

  const getSubmissionData = useCallback(() => {
    return questions.map((question, index) => ({
      id: question.id,
      selected_answer: answers[index] || null,
      question_text: question.question,
      time_spent: Math.floor((Date.now() - examStartTime) / 1000)
    }));
  }, [questions, answers, examStartTime]);

  const canGoNext = currentQuestion < questions.length - 1;
  const canGoPrevious = currentQuestion > 0;
  const isLastQuestion = currentQuestion === questions.length - 1;
  const currentQuestionData = questions[currentQuestion] || null;

  return {
    // State
    questions,
    currentQuestion,
    answers,
    flaggedQuestions,
    isNavigatorVisible,
    currentQuestionData,
    
    // Navigation flags
    canGoNext,
    canGoPrevious,
    isLastQuestion,
    
    // Actions
    initializeExam,
    selectAnswer,
    toggleFlag,
    goToQuestion,
    goToNext,
    goToPrevious,
    toggleNavigator,
    
    // Utilities
    getExamSummary,
    getSubmissionData
  };
};

export default useExamState;